import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostAttributeToken,
  TemplateRef,
  afterNextRender,
  computed,
  inject,
  input,
  model,
  output,
  viewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { DraftGroup, updateDraft } from '@core/filters/path-filter/draft';
import { HoleKind } from '@core/filters/path-filter/placeholders';
import { FilterVocabulary } from '@core/filters/path-filter/vocabulary/vocabulary';
import { OpArgument } from '@core/filters/path-filter/wire';
import { CurrentLangService } from '@core/services/common/current-lang.service';

import { AvailableLanguages } from '@shared/types/translation.type';

import { PathFilterGroupComponent } from '../group/path-filter-group.component';
import { PATH_FILTER_PANEL_PIN } from '../panel/path-filter-panel-pin';
import { injectPathFilterRegistries } from '../path-filter-registries';
import {
  ComposerContext,
  ComposerMode,
  NewParameterRequest,
  ParameterOption
} from '../path-filter.models';
import { describeFilter } from '../phrase/describe-filter';
import { PathFilterPhraseComponent } from '../phrase/path-filter-phrase.component';
import {
  PATH_FILTER_COMPOSER_STATE,
  createComposerState
} from './path-filter-composer-state';

@Component({
  selector: 'app-path-filter-composer',
  imports: [NgTemplateOutlet, PathFilterGroupComponent, PathFilterPhraseComponent],
  templateUrl: './path-filter-composer.component.html',
  providers: [{ provide: PATH_FILTER_COMPOSER_STATE, useFactory: createComposerState }],
  styles: `
    :host {
      display: block;
    }
  `
})
export class PathFilterComposerComponent {
  draft = model.required<DraftGroup>();
  vocabulary = input.required<FilterVocabulary>();
  mode = input<ComposerMode>('data');
  /** Offered by a value slot's `Value | Parameter` switch, filtered to the slot's kind. */
  parameters = input<ParameterOption[]>([]);
  /** The carrier named by `compat` mode's "This ‹record›'s field" switch. */
  selfLabel = input<string | null>(null);
  invalidPaths = input<string[]>([]);
  /** False for a host that describes the filter in a sentence of its own. */
  showPhrase = input(true);

  newParameterRequested = output<NewParameterRequest>();

  private destroyed = false;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly hovered = inject(PATH_FILTER_COMPOSER_STATE).hovered;

  /** Only a composer that is the panel's whole body pins its phrase above the panel's footer. */
  private readonly pin =
    inject(new HostAttributeToken('panel-body'), { optional: true }) === null
      ? null
      : inject(PATH_FILTER_PANEL_PIN, { optional: true });
  protected readonly pinned = this.pin !== null;
  private readonly phrase = viewChild.required<TemplateRef<unknown>>('phrase');

  protected readonly segments = computed(() =>
    describeFilter(
      this.draft(),
      this.vocabulary(),
      this.currentLang() as AvailableLanguages,
      this.translate,
      this.selfLabel()
    )
  );

  private readonly registries = injectPathFilterRegistries();
  protected readonly context = computed<ComposerContext>(() => ({
    vocabulary: this.vocabulary(),
    mode: this.mode(),
    parameters: this.parameters(),
    selfLabel: this.selfLabel(),
    invalidPaths: this.invalidPaths(),
    tags: this.registries.tags(),
    labels: this.registries.labels(),
    requestParameter: (kind, conditionId, toArg) =>
      this.requestParameter(kind, conditionId, toArg)
  }));

  constructor() {
    const pin = this.pin;
    if (pin) afterNextRender(() => pin.phrase.set(this.phrase()));
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      if (pin && pin.phrase() === this.phrase()) pin.phrase.set(null);
    });
  }

  /** The innermost row, block or group under the pointer or holding the focus. */
  protected point(target: EventTarget | null): void {
    this.hovered.set(nodeAt(target));
  }

  /** The pointer leaves; a row that holds the focus stays lit. */
  protected leave(conditions: HTMLElement): void {
    const focused = document.activeElement;
    this.hovered.set(focused && conditions.contains(focused) ? nodeAt(focused) : null);
  }

  protected focusLeaves(event: FocusEvent, conditions: HTMLElement): void {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !conditions.contains(next)) this.hovered.set(null);
  }

  private requestParameter(
    kind: HoleKind,
    conditionId: string,
    toArg: (name: string) => OpArgument | null
  ): void {
    this.newParameterRequested.emit({
      kind,
      // Applied to the draft as it is when the host answers: other edits may have happened since,
      // and a condition removed meanwhile is simply not found. A composer closed meanwhile has no
      // draft left to write, and its `draftChange` can no longer emit.
      resolve: (name) => {
        if (this.destroyed) return;
        this.draft.update((root) =>
          updateDraft(root, conditionId, (node) =>
            node.kind === 'condition' ? { ...node, arg: toArg(name) } : node
          )
        );
      }
    });
  }
}

function nodeAt(target: EventTarget | null): string | null {
  return target instanceof Element
    ? (target.closest('[data-path-node]')?.getAttribute('data-path-node') ?? null)
    : null;
}
